import { currencyCodeSuffix } from '../../utils/money';
import { useBookingLocale } from '../../hooks/useBookingLocale';
// PaymentHandler.tsx component
import { Elements, useStripe } from '@stripe/react-stripe-js';
import { useStripePromise } from '../../hooks/useStripePromise';
import React, { useEffect, useState } from 'react';
import {
  useBookingPaymentOptionsQuery,
  useCreateTripExtensionPaymentMutation,
} from '../../services/stripeService/__generated__/queries.generated';
import { Modal } from '../modal';
import { ApolloProvider } from '@apollo/client';
import { client } from '../../services/apollo';
import { Button } from '../button';
import { toast } from 'react-toastify';
import { formatInstantInZoneWithAbbr } from '../../helpers/timezoneHelpers';
import styled from 'styled-components';
import tw from 'twin.macro';
import {
  useCalculateTripExtensionMutation,
  useConfirmTripExtensionMutation,
} from '../../services/bookingService/__generated__/queries.generated';
import { useCreateShortenRefundInvoiceMutation } from '../../services/invoiceServices/__generated__/queries.generated';
import {
  EXTENSION_BLOCKED_MESSAGE,
  initialCardChoice,
  NEW_CARD_CHOICE,
  nextCardAfterDecline,
  nextExtensionPaymentStep,
  extensionRequestErrorMessage,
  serverPriceDiffers,
} from './extensionPayment';
import { ExtensionCardPicker } from './ExtensionCardPicker';
import { NewCardForm } from './NewCardForm';

const FormContainer = styled.div`
  ${tw`flex flex-col items-center justify-between h-full`}
`;

const ErrorNotice = styled.p`
  ${tw`text-sm rounded-md px-3 py-2 my-2`}
  color: #b91c1c;
  background: #fef2f2;
`;

interface PaymentHandlerProps {
  bookingId: string;
  /** Card to charge. Empty: the server uses the booking's preferred card. */
  paymentMethodId: string;
  amount: number;
  originalEndAt: number;
  newEndAt: number;
  /** IANA zone of the car location. Display and notes must use this, not the browser TZ. */
  timezone?: string | null;
  shortenTripPenalty?: number | null;
  freeCancellationServiceFee?: number | null;
  isExtendTripPaymentModalOpen: boolean;
  onClose: () => void;
  refetchBooking: () => void;
  refetchBookingActivities?: () => void | Promise<unknown>;
}

const ExtendTripPaymentHandler: React.FC<PaymentHandlerProps> = ({
  bookingId,
  paymentMethodId,
  amount,
  originalEndAt,
  newEndAt,
  timezone,
  shortenTripPenalty,
  freeCancellationServiceFee,
  onClose,
  refetchBooking,
  refetchBookingActivities,
}) => {
  const stripe = useStripe();
  // Spec 028 D14: charges and refunds carry the code for a CAD trip.
  const code = currencyCodeSuffix(useBookingLocale(bookingId, 'any').currency);
  const [paymentError, setPaymentError] = useState<string | null>(null);
  const [blockedByDecline, setBlockedByDecline] = useState(false);
  const [paying, setPaying] = useState(false);

  // Spec 013 P3: the guest picks the card; after a decline the next card (or a new one)
  // is selected so another try is one tap away.
  const isExtension = amount > 0;
  const {
    data: optionsData,
    loading: optionsLoading,
    refetch: refetchOptions,
  } = useBookingPaymentOptionsQuery({
    variables: { bookingId },
    skip: !isExtension || !bookingId,
    fetchPolicy: 'network-only',
  });
  const options = optionsData?.bookingPaymentOptions;
  const cards = options?.cards ?? [];
  const [cardChoice, setCardChoice] = useState<string | null>(null);
  const [newCardFormKey, setNewCardFormKey] = useState(0);
  useEffect(() => {
    if (options && cardChoice === null) {
      setCardChoice(
        initialCardChoice(options.cards, options.preferredPaymentMethodId, paymentMethodId),
      );
    }
  }, [options, cardChoice, paymentMethodId]);
  const extensionBlocked = blockedByDecline || Boolean(options?.extensionBlocked);

  // Spec 013 D6: the server prices and charges the extension; the client only completes
  // a bank challenge and then asks the server to verify the intent and extend the trip.
  const [createTripExtensionPayment] = useCreateTripExtensionPaymentMutation();

  const [confirmTripExtension, { loading: confirmTripExtensionLoading }] =
    useConfirmTripExtensionMutation({
      onCompleted: () => {
        // Refetch booking after successful trip extension
        setTimeout(() => {
          refetchBooking();
          void refetchBookingActivities?.();
        }, 500);
      },
    });

  const [createShortenRefundInvoice, { loading: createShortenRefundInvoiceLoading }] =
    useCreateShortenRefundInvoiceMutation({
      onCompleted: () => {
        setTimeout(() => {
          refetchBooking();
          void refetchBookingActivities?.();
        }, 1000);
      },
      onError: error => {
        const errorMessage = error.message || 'Unknown error occurred';
        toast.error(`Failed to create shorten refund invoice: ${errorMessage}`);
      },
    });

  const listingZone = timezone?.trim() || 'America/Los_Angeles';
  const formatListingInstant = (ms: number) => formatInstantInZoneWithAbbr(ms, listingZone);

  const confirmExtension = async (paymentIntentId: string, serverAmount: number) => {
    await confirmTripExtension({
      variables: {
        input: {
          bookingId,
          newEndAt,
          calculatedPriceDifference: serverAmount,
          shortenTripPenalty: shortenTripPenalty ?? 0,
          paymentIntentId,
        },
      },
    });
  };

  const payForExtension = async (chosenPaymentMethodId: string | null) => {
    if (!stripe) return;
    setPaying(true);
    setPaymentError(null);
    try {
      const { data } = await createTripExtensionPayment({
        variables: {
          input: {
            bookingId,
            newEndAt,
            paymentMethodId: chosenPaymentMethodId || undefined,
            platform: 'web',
          },
        },
      });
      const result = data?.createTripExtensionPayment;
      if (!result) throw new Error('No answer from the payment service');

      const step = nextExtensionPaymentStep(result);
      if (step.kind === 'failed') {
        setPaymentError(step.message);
        setBlockedByDecline(step.blocked);
        if (!step.blocked) moveToAnotherCard(chosenPaymentMethodId);
        return;
      }
      if (serverPriceDiffers(amount, step.amount)) {
        toast.info(`The extension price is $${step.amount.toFixed(2)}${code}.`);
      }

      let paymentIntentId = step.paymentIntentId;
      if (step.kind === 'authenticate') {
        // The card is already attached to the intent; the bank wants the guest to confirm.
        const challenge = await stripe.confirmCardPayment(step.clientSecret);
        if (challenge.error) {
          setPaymentError(challenge.error.message || 'The bank did not approve this payment.');
          moveToAnotherCard(chosenPaymentMethodId);
          return;
        }
        if (challenge.paymentIntent?.status !== 'succeeded') {
          setPaymentError('The payment was not completed. Please try again.');
          return;
        }
        paymentIntentId = challenge.paymentIntent.id;
      }

      await confirmExtension(paymentIntentId, step.amount);
      toast.success('Trip extended successfully');
      onClose();
    } catch (error) {
      // A refused request (HOST_LISTINGS_PAUSED, PAYMENT_NOT_VERIFIED…) shows the server's words.
      setPaymentError(extensionRequestErrorMessage(error));
    } finally {
      setPaying(false);
    }
  };

  /** After a failed try: a fresh new-card form, or the next listed card. */
  function moveToAnotherCard(failedPaymentMethodId: string | null) {
    void refetchOptions().catch(() => undefined);
    if (cardChoice === NEW_CARD_CHOICE) {
      setNewCardFormKey(key => key + 1);
      return;
    }
    if (failedPaymentMethodId) setCardChoice(nextCardAfterDecline(cards, failedPaymentMethodId));
  }

  const [makeSureTripShorteningIsAllowed, { loading: calculateTripExtensionLoading }] =
    useCalculateTripExtensionMutation({
      onCompleted: async () => {
        if (!bookingId) return;
        try {
          await createShortenRefundInvoice({
            variables: {
              input: {
                bookingId,
                amount: Math.abs(amount),
                note: `shorten trip from ${formatListingInstant(
                  originalEndAt,
                )} to ${formatListingInstant(newEndAt)}`,
                newEndAt,
              },
            },
          });
        } catch (error) {
          toast.error(error instanceof Error ? error.message : 'Modification failed');
          onClose();
          return;
        }
        toast.success('Trip shortened successfully');
        onClose();
      },
      onError: (error: Error) => {
        toast.error(`Failed to modify trip + ${error.message}`);
        onClose();
      },
    });

  const handlePayment = async () => {
    if (isExtension) {
      await payForExtension(cardChoice && cardChoice !== NEW_CARD_CHOICE ? cardChoice : null);
      return;
    }
    makeSureTripShorteningIsAllowed({
      variables: {
        input: {
          bookingId,
          newEndAt,
        },
      },
    });
  };

  const titleContent = amount > 0 ? 'Extend Trip Payment' : 'Confirm Modification';
  const isShortening = amount < 0;
  const rawRefundAmount = Math.abs(amount);
  const serviceFee = freeCancellationServiceFee ?? 0;
  const actualRefundAmount = rawRefundAmount - serviceFee;
  const busy =
    paying ||
    calculateTripExtensionLoading ||
    confirmTripExtensionLoading ||
    createShortenRefundInvoiceLoading;

  return (
    <FormContainer>
      <div>
        <h1>{titleContent}</h1>
        {isShortening ? (
          <>
            <p>
              Total Refund: ${rawRefundAmount.toFixed(2)}
              {code}
            </p>
            {shortenTripPenalty ? (
              <p>Shorten Trip Penalty: ${shortenTripPenalty.toFixed(2)}</p>
            ) : null}
            {serviceFee > 0 ? <p>Service Fee: ${serviceFee.toFixed(2)}</p> : null}
            <p>
              <strong>
                Actual Refund: ${actualRefundAmount.toFixed(2)}
                {code}
              </strong>
            </p>
          </>
        ) : (
          <p>
            Amount to Pay: ${amount.toFixed(2)}
            {code}
          </p>
        )}
        <p>New End At: {formatListingInstant(newEndAt)}</p>
        <p style={{ fontSize: '12px', color: 'var(--color-text-secondary)' }}>
          Booking Id: {bookingId}
        </p>
        {paymentError ? <ErrorNotice role="alert">{paymentError}</ErrorNotice> : null}
        {isExtension && extensionBlocked && !paymentError ? (
          <ErrorNotice role="alert">{EXTENSION_BLOCKED_MESSAGE}</ErrorNotice>
        ) : null}
        {isExtension && options && !extensionBlocked ? (
          <ExtensionCardPicker
            cards={cards}
            value={cardChoice}
            onChange={choice => {
              setCardChoice(choice);
              setPaymentError(null);
            }}
            disabled={busy}
          />
        ) : null}
        {isExtension && cardChoice === NEW_CARD_CHOICE && !extensionBlocked ? (
          <NewCardForm
            key={newCardFormKey}
            bookingId={bookingId}
            busy={busy}
            disabled={extensionBlocked}
            onCardReady={payForExtension}
          />
        ) : null}
      </div>
      {isExtension && cardChoice === NEW_CARD_CHOICE && !extensionBlocked ? null : (
        <Button
          onClick={handlePayment}
          text={isExtension ? (paymentError ? 'Try again' : 'Pay') : 'Confirm Modification'}
          isLoading={busy}
          isDisabled={busy || extensionBlocked || (isExtension && optionsLoading)}
        />
      )}
    </FormContainer>
  );
};

// Wrapper component that provides Stripe context
const ExtendTripPaymentModal: React.FC<PaymentHandlerProps> = props => {
  const { stripePromise, publishableKey, ready } = useStripePromise();
  return (
    <Modal isOpen={props.isExtendTripPaymentModalOpen} onClose={props.onClose} modalHeight="auto">
      <ApolloProvider client={client}>
        {ready ? (
          <Elements stripe={stripePromise} key={publishableKey}>
            <ExtendTripPaymentHandler {...props} />
          </Elements>
        ) : null}
      </ApolloProvider>
    </Modal>
  );
};

export default ExtendTripPaymentModal;
